/**
 * The START_AUDIO / welcome-CTA unlock path over the SDK surface — the vanilla
 * port of the React StartAudioListener (ADR 0039). Owns two subtleties the
 * SDK's raw `audioPlaybackChanged` signal deliberately does not:
 *
 * - COMPOSITE PLAYABILITY: `getAutoplayPolicyVerdict()` (Chrome-authoritative)
 *   vetoes LiveKit's optimistic `canPlaybackAudio`; Safari (no policy API)
 *   falls back to the LiveKit flag alone.
 * - POST-SETTLE REPORTING: reports are suppressed until `startAudio()` has
 *   SETTLED for the current request, so an early playback-status emit can't
 *   leak a premature value (on Safari `canPlaybackAudio` briefly flips false
 *   before startAudio resolves; on cold Chrome it's optimistically true before
 *   the policy veto applies). Re-armed on every request so a CTA re-tap
 *   settles afresh; only the LATEST request reports on settle.
 *
 * Also owns request queueing: a START_AUDIO arriving before the SDK is
 * connected is remembered and replayed on `notifyConnected()` (the old
 * counter-effect's "re-run when the room appears" behavior).
 */

import { getAutoplayPolicyVerdict } from '../core/logic/audio-availability.js';
import { flowLog } from '../welcome-flow-log.js';
import { getLogger } from '../logger.js';

const logger = getLogger('AudioUnlock');

export interface AudioUnlockDeps {
  /** The SDK's audio unlock (must be called while a user gesture is active). */
  startAudio(): Promise<void>;
  /** LiveKit's current playback flag (SDK snapshot). */
  canPlaybackAudio(): boolean;
  /** Authoritative audio-playable report for the welcome gate. */
  report(canPlay: boolean): void;
}

export interface AudioUnlock {
  /** START_AUDIO from the parent or a welcome-CTA tap. Queued until connected. */
  request(): void;
  /** The SDK connected (or re-connected) — replay a queued request. */
  notifyConnected(): void;
  /** The transport dropped: requests queue again until `notifyConnected()` (#478). */
  notifyDisconnected(): void;
  /** Wire to the SDK's `audioPlaybackChanged` event (post-settle re-gate). */
  handlePlaybackChanged(): void;
}

/** Composite "is the dealer's audio genuinely playable right now?" — see module docs. */
function audioPlayable(deps: AudioUnlockDeps): boolean {
  if (getAutoplayPolicyVerdict() === true) {
    return false; // Chrome-authoritative: audible autoplay is blocked.
  }
  return deps.canPlaybackAudio(); // Chrome-allowed / Safari: trust LiveKit's real playback flag.
}

export function createAudioUnlock(deps: AudioUnlockDeps): AudioUnlock {
  let connected = false;
  let pending = false;
  let settled = false;
  let seq = 0;

  const run = (): void => {
    const requestId = ++seq;
    settled = false; // re-arm: don't report this request until startAudio settles
    flowLog(`sdk.startAudio() invoking (request=${requestId})`);
    deps
      .startAudio()
      .catch((error) => {
        flowLog(
          `sdk.startAudio() REJECTED: ${error instanceof Error ? error.message : String(error)}`
        );
        logger.warn('Failed to start audio playback on request', {
          error: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => {
        // startAudio settled → canPlaybackAudio now reflects a real <audio> attempt.
        // A newer request supersedes this one — only the latest reports.
        if (requestId !== seq) return;
        settled = true;
        const playable = audioPlayable(deps);
        flowLog(
          `startAudio settled → reporting audioPlayable=${playable} (canPlaybackAudio=${deps.canPlaybackAudio()})`
        );
        deps.report(playable);
      });
  };

  return {
    request(): void {
      if (!connected) {
        flowLog('START_AUDIO pending — SDK not connected yet (replayed on connect)');
        pending = true;
        return;
      }
      run();
    },

    notifyConnected(): void {
      connected = true;
      if (pending) {
        pending = false;
        run();
      }
    },

    notifyDisconnected(): void {
      connected = false;
    },

    handlePlaybackChanged(): void {
      if (!settled) {
        return; // pre-settle: the startAudio().finally above is the first authoritative report
      }
      const playable = audioPlayable(deps);
      flowLog(`audioPlaybackChanged → audioPlayable=${playable} (post-settle re-gate)`);
      deps.report(playable);
    },
  };
}
